import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControlLabel,
    Switch,
    Divider,
    Alert,
    CircularProgress,
    Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNotifications } from '../context/NotificationContext';

/**
 * Styled components
 */
const PreferencesContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const PreferenceSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
}));

const SavedIndicator = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.success.main,
    fontSize: '0.875rem',
    fontWeight: 500,
}));

/**
 * NotificationPreferences Component
 * Manages user notification preferences for the current project
 *
 * @param {boolean} hideSaveIndicators - Hide individual save indicators when used in dialogs/modals
 *                                      that have their own unified save state management
 * @param {boolean} hideAutoSaveMessage - Hide the auto-save informational message
 */
const NotificationPreferences = ({
    hideSaveIndicators = false,
    hideAutoSaveMessage = false,
    hideHeading = false,
}) => {
    const { preferences, updatePreferences, isUpdatingPreferences, preferencesError } =
        useNotifications();

    const [showSavedIndicator, setShowSavedIndicator] = useState(false);
    const [wasUpdating, setWasUpdating] = useState(false);

    // Provide safe defaults to prevent undefined errors during initialization
    const safePreferences = preferences || {
        emailEnabled: true,
        inAppEnabled: true,
    };

    const handlePreferenceChange = (field) => (event) => {
        const newPreferences = {
            ...safePreferences,
            [field]: event.target.checked,
        };
        updatePreferences(newPreferences);
    };

    // Show "Saved ✓" indicator only when an actual update completes successfully
    useEffect(() => {
        // Skip if save indicators are hidden
        if (hideSaveIndicators) return;

        // Track if we were previously updating
        if (isUpdatingPreferences) {
            setWasUpdating(true);
        }

        // Only show saved indicator if we were updating and now we're not (completed update)
        if (wasUpdating && !isUpdatingPreferences && preferences && !preferencesError) {
            setShowSavedIndicator(true);
            setWasUpdating(false);

            const timer = setTimeout(() => {
                setShowSavedIndicator(false);
            }, 3000); // Show for 3 seconds

            return () => clearTimeout(timer);
        }

        // Reset wasUpdating if there was an error
        if (!isUpdatingPreferences && preferencesError) {
            setWasUpdating(false);
        }
    }, [isUpdatingPreferences, preferences, preferencesError, wasUpdating, hideSaveIndicators]);

    // Show loading state if preferences are not yet loaded
    if (!preferences) {
        return (
            <PreferencesContainer>
                {!hideHeading && (
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Notification Preferences
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', py: 4 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        Loading preferences...
                    </Typography>
                </Box>
            </PreferencesContainer>
        );
    }

    return (
        <PreferencesContainer>
            {!hideSaveIndicators ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                    }}
                >
                    {!hideHeading && (
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Notification Preferences
                        </Typography>
                    )}

                    {/* Save Status Indicators */}
                    <Box sx={{ minHeight: '24px', display: 'flex', alignItems: 'center' }}>
                        {isUpdatingPreferences && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    Saving...
                                </Typography>
                            </Box>
                        )}

                        <Fade in={showSavedIndicator} timeout={300}>
                            <SavedIndicator>
                                <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Saved
                            </SavedIndicator>
                        </Fade>
                    </Box>
                </Box>
            ) : (
                <Box>
                    {!hideHeading && (
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0 }}>
                            Notification Preferences
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0, lineHeight: 1.4, mb: 2 }}
                    >
                        Set you personal preferences for notifications.
                    </Typography>
                </Box>
            )}

            {!hideAutoSaveMessage && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Changes are saved immediately when you toggle these settings.
                </Typography>
            )}

            {preferencesError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {preferencesError}
                </Alert>
            )}

            {/* In-App Notifications */}
            <PreferenceSection>
                <SectionTitle variant="subtitle1">In-App Notifications</SectionTitle>
                <FormControlLabel
                    control={
                        <Switch
                            checked={safePreferences.inAppEnabled}
                            onChange={handlePreferenceChange('inAppEnabled')}
                            disabled={isUpdatingPreferences}
                        />
                    }
                    label="Show notifications in the application"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                    Receive notifications directly in the app
                </Typography>
            </PreferenceSection>

            <Divider sx={{ my: 2 }} />

            {/* Email Notifications */}
            <PreferenceSection>
                <SectionTitle variant="subtitle1">Email Notifications</SectionTitle>
                <FormControlLabel
                    control={
                        <Switch
                            checked={safePreferences.emailEnabled}
                            onChange={handlePreferenceChange('emailEnabled')}
                            disabled={isUpdatingPreferences}
                        />
                    }
                    label="Enable email notifications"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                    Receive notifications via email
                </Typography>
            </PreferenceSection>
        </PreferencesContainer>
    );
};

export default NotificationPreferences;
